import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import {
  bookSummary,
  checkProgress,
  confirmAll,
  fillEmptyPrices,
  goodsValueCents,
  hasPrices,
  isBookable,
  toBookLines,
  withPriceInput,
} from '../lib/delivery-lines'
import { useLastPrices } from '../hooks/use-deliveries'
import {
  DeliveryCheckHeader,
  DeliveryCheckLineRow,
  GoodsValueFooter,
  LINES_CONTAINER,
} from './delivery-line-editor'
import type { CheckLine } from '../lib/delivery-lines'
import type { DeliveryBookLine } from '@/api/endpoints/inventory-deliveries'
import type { StockArticleInfo } from '@/features/inventory/hooks/use-stock-info'
import type { SelectedProduct } from '@/features/products/components/product-sku-combobox'
import { ProductSkuCombobox } from '@/features/products/components/product-sku-combobox'
import { useCountSessionGuard } from '@/features/inventory-count/hooks/use-count-session-guard'
import {
  stockInfoFromFields,
  useStockInfo,
} from '@/features/inventory/hooks/use-stock-info'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * A line as the view works with it: the counting mode from the article (D9), the unit and the
 * no-stock flag from the server's line, else from the article (a position added at the door).
 */
export function withStockInfo(
  line: CheckLine,
  info: StockArticleInfo | undefined,
): CheckLine {
  // The line's own data wins; the article's facts only fill what the line does not say.
  if (!info) return line
  return {
    ...line,
    fractional: line.fractional ?? info.fractional,
    unit: line.unit ?? info.unit,
    noStock: line.noStock ?? info.noStock ?? undefined,
  }
}

interface DeliveryCheckViewProps {
  initialLines: Array<CheckLine>
  restaurantId: number
  canWrite: boolean
  /**
   * A hand-announced delivery: its net prices per piece are entered here, next to the last
   * booked price (task 9.5). An imported delivery's prices are the Lieferschein's, read-only.
   */
  manualPrices?: boolean
  booking: boolean
  onBook: (lines: Array<DeliveryBookLine>) => void
}

/**
 * The check-and-book view of an ANNOUNCED delivery.
 *
 * Received quantities stay local until the single book request: an empty field means "not yet
 * checked" and blocks booking, `0` means "did not arrive". Removing and adding positions is
 * likewise local and undone by simply leaving the page.
 */
export function DeliveryCheckView({
  initialLines,
  restaurantId,
  canWrite,
  manualPrices = false,
  booking,
  onBook,
}: DeliveryCheckViewProps) {
  const { t } = useTranslation('inventory-deliveries')
  const priceEditable = canWrite && manualPrices
  const [rawLines, setLines] = useState<Array<CheckLine>>(() =>
    priceEditable ? initialLines.map(withPriceInput) : initialLines,
  )
  const [confirmOpen, setConfirmOpen] = useState(false)
  const countGuard = useCountSessionGuard(restaurantId)
  // CHG-2026-09-stock-usage-and-price-twins: decimals only for articles with "Teilmengen zählen";
  // until the articles are known every line takes whole numbers, as before.
  // Only a line that does not carry its counting mode is looked up (a backend before phase B4).
  const stockInfo = useStockInfo(
    rawLines.filter((l) => l.fractional === undefined).map((l) => l.sku),
  )
  const lines = useMemo(
    () => rawLines.map((l) => withStockInfo(l, stockInfo.get(l.sku))),
    [rawLines, stockInfo],
  )

  const progress = checkProgress(lines)
  const summary = bookSummary(lines)
  const bookable = canWrite && !booking && isBookable(lines)
  // The price columns exist where there is something to show or to enter: an imported delivery
  // with Lieferschein prices, or a hand-announced one whose prices are typed here.
  const showPrices = priceEditable || hasPrices(lines)
  const goodsValue = goodsValueCents(lines)
  const lastPrices = useLastPrices(
    priceEditable ? restaurantId : null,
    priceEditable ? lines.map((l) => l.sku) : [],
  )

  const updateLine = (sku: string, next: CheckLine) =>
    setLines((prev) =>
      prev.map((l) =>
        l.sku === sku
          ? {
              ...l,
              received: next.received,
              note: next.note,
              ...(l.price === undefined ? {} : { price: next.price }),
            }
          : l,
      ),
    )

  const removeLine = (sku: string) =>
    setLines((prev) => prev.filter((l) => l.sku !== sku))

  const addLine = (product: SelectedProduct | null) => {
    if (!product) return
    // A product picked here carries its own stock facts (the product list), so it needs no lookup.
    const info = stockInfoFromFields(product)
    setLines((prev) =>
      prev.some((l) => l.sku === product.sku)
        ? prev
        : [
            ...prev,
            {
              sku: product.sku,
              name: product.name,
              // Unannounced goods: the whole received quantity reads as a surplus. Nobody typed a
              // purchase price for it either, so it stays out of the goods value.
              expectedQty: 0,
              received: '',
              note: '',
              ...(info
                ? {
                    fractional: info.fractional,
                    unit: info.unit,
                    ...(info.noStock ? { noStock: info.noStock } : {}),
                  }
                : {}),
              unitLevel: null,
              unitName: null,
              unitCount: null,
              contentPerUnit: null,
              unitCostCents: null,
              ...(priceEditable ? { price: '' } : {}),
            },
          ],
    )
  }

  const handleFillLastPrices = () =>
    setLines((prev) =>
      fillEmptyPrices(prev, (l) => lastPrices.get(l.sku)?.unitCostCents),
    )

  const handleConfirmAll = () => {
    setLines((prev) =>
      confirmAll(prev.map((l) => withStockInfo(l, stockInfo.get(l.sku)))),
    )
    toast.success(t('toast.confirmedAll'))
  }

  return (
    <div className="flex flex-col gap-4 pb-24">
      <Card className="overflow-hidden py-0">
        <CardContent className="p-0">
          {canWrite && (
            <div className="flex flex-wrap justify-end gap-2 border-b px-3 py-2">
              {/* Act on the table, so they live with the table: fill only the untouched fields. */}
              {priceEditable && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={lastPrices.size === 0}
                  onClick={handleFillLastPrices}
                >
                  {t('price.copyAll')}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleConfirmAll}
              >
                {t('detail.confirmAll')}
              </Button>
            </div>
          )}
          <div className={LINES_CONTAINER}>
            <DeliveryCheckHeader readOnly={!canWrite} showPrices={showPrices} />
            {lines.map((line) => (
              <DeliveryCheckLineRow
                key={line.sku}
                line={line}
                readOnly={!canWrite}
                showPrices={showPrices}
                lastPrice={lastPrices.get(line.sku)}
                onChange={(next) => updateLine(line.sku, next)}
                onRemove={() => removeLine(line.sku)}
              />
            ))}
          </div>
          {/* The net goods value follows the quantities and prices as they are entered, so a
              corrected position is reflected before the booking, not only after it. */}
          {goodsValue != null && (
            <GoodsValueFooter
              label={t('detail.goodsValue')}
              cents={goodsValue}
            />
          )}
          {canWrite && (
            <div className="border-t p-3">
              <div className="max-w-md">
                <ProductSkuCombobox
                  value={null}
                  onChange={addLine}
                  placeholder={t('detail.addLine')}
                  restaurantId={restaurantId}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {canWrite && (
        <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center justify-between gap-3 border-t bg-background px-4 py-3 sm:mx-0 sm:rounded-md sm:border sm:px-4">
          <p className="text-sm text-muted-foreground">
            {t('detail.progress', {
              done: progress.done,
              total: progress.total,
            })}
          </p>
          <Button
            type="button"
            size="lg"
            disabled={!bookable || countGuard.checking}
            onClick={() => setConfirmOpen(true)}
          >
            {t('detail.book')}
          </Button>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('book.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-2">
                <p>
                  {t('book.confirmBody', {
                    lines: summary.lineCount,
                    units: formatStockDisplay(summary.units),
                  })}
                </p>
                {summary.deviating > 0 && (
                  <p>
                    {t('book.confirmDeviations', { count: summary.deviating })}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('book.confirmCancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false)
                const bookLines = toBookLines(lines)
                void countGuard.guard(() => onBook(bookLines))
              }}
            >
              {t('book.confirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {countGuard.dialog}
    </div>
  )
}
